import { useAuth } from "@/hooks/use-auth";
import { useLocation } from "wouter";
import { useEffect, useRef, useMemo } from "react";

interface ProtectedRouteProps {
  children: React.ReactNode;
  allowedRoles?: string[];
  redirectTo?: string;
  autoRedirectDrivers?: boolean;
  autoRedirectHospitalPartners?: boolean;
  autoRedirectEmployerAdmins?: boolean;
}

export function ProtectedRoute({ 
  children, 
  allowedRoles, 
  redirectTo = "/",
  autoRedirectDrivers = false,
  autoRedirectHospitalPartners = false,
  autoRedirectEmployerAdmins = false
}: ProtectedRouteProps) {
  const { user, isLoading } = useAuth();
  const [, setLocation] = useLocation();
  const hasRedirected = useRef(false);

  const isAuthorized = useMemo(() => {
    if (!user) return false;
    if (!allowedRoles) return true;
    return allowedRoles.includes(user.role);
  }, [user, allowedRoles]);

  useEffect(() => {
    if (!isLoading && user && !hasRedirected.current) {
      if (autoRedirectDrivers && user.role === "driver") {
        hasRedirected.current = true;
        setLocation("/driver");
      } else if (autoRedirectHospitalPartners && user.role === "hospital_partner_admin") {
        hasRedirected.current = true;
        setLocation("/partner-portal");
      } else if (autoRedirectEmployerAdmins && user.role === "employer_admin") {
        hasRedirected.current = true;
        setLocation("/employer");
      } else if (!isAuthorized) {
        hasRedirected.current = true;
        setLocation(redirectTo);
      }
    }
  }, [user, isLoading, isAuthorized, redirectTo, setLocation, autoRedirectDrivers, autoRedirectHospitalPartners, autoRedirectEmployerAdmins]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  if (!isAuthorized) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">Redirecting...</div>
      </div>
    );
  }

  return <>{children}</>;
}
